import React, { useMemo } from 'react';
import { FaTimes, FaSave } from 'react-icons/fa';
import { exportToExcel } from '../../../services/ExcelExportService';
import { Operation } from './OperationPageViewBackend';

interface ExportPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    operation: Operation;
}

const buildExportData = (operation: Operation) => {
    const rows: any[] = [];
    operation.documents.forEach(doc => {
        doc.receivables.forEach(receivable => {
            rows.push({
                indPag: receivable.ind_pag || '',
                natOp: receivable.nat_op || '',
                vPag: receivable.v_pag || '',
                operacao: operation.operation_code,
                cedente_nome: doc.assignor_name,
                cedente_cnpj: doc.assignor_cnpj,
                nfe: doc.document_id,
                titulo: doc.document_title,
                cpf_cnpj: doc.debtor_cpf_cnpj,
                nome: doc.debtor_name,
                logradouro: doc.address_street,
                numero: doc.address_number,
                complemento: doc.address_complement,
                bairro: doc.address_district,
                cod_ibge: doc.address_ibge_code,
                municipio: doc.address_city,
                uf: doc.address_state,
                cep: doc.address_zip_code,
                pais: doc.address_country,
                telefone: doc.address_phone,
                email: doc.debtor_email,
                cfop: doc.cfop,
                data_emissao: doc.issue_date ? doc.issue_date.split('T')[0].split('-').reverse().join('/') : '',
                ndup: receivable.receivable_number,
                vdup: receivable.amount,
                dvenc: receivable.due_date ? receivable.due_date.split('T')[0].split('-').reverse().join('/') : '',
                status: doc.processing_status || '',
                erro: doc.processing_error || '',
                valor_corrigido: '',
                vencimento_corrigido: '',
                parcela_adquirida: '',
            });
        });
    });
    return rows;
};

const HEADERS = [
    { id: 'indPag', title: 'indPag', field: 'indPag', canSort: false },
    { id: 'natOp', title: 'natOp', field: 'natOp', canSort: false },
    { id: 'vPag', title: 'vPag', field: 'vPag', canSort: false },
    { id: 'operacao', title: 'Operação', field: 'operacao', canSort: false },
    { id: 'cedente_nome', title: 'Cedente Nome', field: 'cedente_nome', canSort: false },
    { id: 'cedente_cnpj', title: 'Cedente CNPJ', field: 'cedente_cnpj', canSort: false },
    { id: 'nfe', title: 'NFe', field: 'nfe', canSort: false },
    { id: 'titulo', title: 'Titulo', field: 'titulo', canSort: false },
    { id: 'cpf_cnpj', title: 'Sacado CNPJ/CPF', field: 'cpf_cnpj', canSort: false },
    { id: 'nome', title: 'Sacado Nome', field: 'nome', canSort: false },
    { id: 'logradouro', title: 'Logradouro', field: 'logradouro', canSort: false },
    { id: 'numero', title: 'Numero', field: 'numero', canSort: false },
    { id: 'complemento', title: 'Complemento', field: 'complemento', canSort: false },
    { id: 'bairro', title: 'bairro', field: 'bairro', canSort: false },
    { id: 'cod_ibge', title: 'CodIBGE', field: 'cod_ibge', canSort: false },
    { id: 'municipio', title: 'municipio', field: 'municipio', canSort: false },
    { id: 'uf', title: 'UF', field: 'uf', canSort: false },
    { id: 'cep', title: 'CEP', field: 'cep', canSort: false },
    { id: 'pais', title: 'Pais', field: 'pais', canSort: false },
    { id: 'telefone', title: 'Telefone', field: 'telefone', canSort: false },
    { id: 'email', title: 'Email', field: 'email', canSort: false },
    { id: 'cfop', title: 'CFOP', field: 'cfop', canSort: false },
    { id: 'data_emissao', title: 'Data Emissão', field: 'data_emissao', canSort: false },
    { id: 'ndup', title: 'nDup', field: 'ndup', canSort: false },
    { id: 'vdup', title: 'vDup', field: 'vdup', canSort: false },
    { id: 'dvenc', title: 'dVenc', field: 'dvenc', canSort: false },
    { id: 'status', title: 'Status', field: 'status', canSort: false },
    { id: 'erro', title: 'Erro', field: 'erro', canSort: false },
    { id: 'valor_corrigido', title: 'Valor Corrigido', field: 'valor_corrigido', canSort: false },
    { id: 'vencimento_corrigido', title: 'vencimento corrigido', field: 'vencimento_corrigido', canSort: false },
    { id: 'parcela_adquirida', title: 'Parcela adquirida', field: 'parcela_adquirida', canSort: false },
];

const ExportPreviewModal: React.FC<ExportPreviewModalProps> = ({ isOpen, onClose, operation }) => {
    const exportData = useMemo(() => buildExportData(operation), [operation]);

    const totalVdup = useMemo(() => exportData.reduce((sum, row) => sum + (Number(row.vdup) || 0), 0), [exportData]);

    const handleSave = () => {
        exportToExcel(HEADERS, exportData, `operacao-${operation.operation_code}.csv`);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg w-[95vw] h-[85vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold dark:text-white">
                        Prévia da Exportação — {operation.operation_code} ({exportData.length} linhas)
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-4">
                    <table className="w-full text-xs border-collapse">
                        <thead className="sticky top-0 bg-gray-100 dark:bg-slate-700">
                            <tr>
                                {HEADERS.map(h => (
                                    <th key={h.id} className="px-2 py-2 text-left font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 whitespace-nowrap">
                                        {h.title}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {exportData.map((row, i) => (
                                <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-700/50'}>
                                    {HEADERS.map(h => (
                                        <td key={h.id} className="px-2 py-1 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                            {row[h.field] ?? ''}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-between items-center gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Soma total vDup: <strong className="text-gray-900 dark:text-white">R$ {totalVdup.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                    </span>
                    <div className="flex gap-3">
                        <button onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 rounded text-sm">
                            Fechar
                        </button>
                        <button onClick={handleSave} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm flex items-center gap-2">
                            <FaSave /> Gerar arquivo Excel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportPreviewModal;
